import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { Palette, Upload, Trash2, Plus, FileSignature, Stamp, FileImage, Building2, RotateCcw, RotateCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const ASSET_META = {
  header: { label: 'Letterhead Header', description: 'Top banner image on every certificate (JPG/PNG).', icon: FileImage, accept: '.jpg,.jpeg,.png' },
  footer: { label: 'Letterhead Footer', description: 'Bottom banner image on every certificate (JPG/PNG).', icon: FileImage, accept: '.jpg,.jpeg,.png' },
  stamp: { label: 'Company Stamp', description: 'Transparent PNG of the company stamp (placed next to signature).', icon: Stamp, accept: '.png' },
};

// Render an image (File or URL) rotated by `degrees` to a PNG Blob.
const rotateImageToBlob = (src, degrees) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    const norm = ((degrees % 360) + 360) % 360;
    const swap = norm === 90 || norm === 270;
    const canvas = document.createElement('canvas');
    canvas.width = swap ? img.height : img.width;
    canvas.height = swap ? img.width : img.height;
    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(norm * Math.PI / 180);
    ctx.drawImage(img, -img.width / 2, -img.height / 2);
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not process image'))), 'image/png');
  };
  img.onerror = () => reject(new Error('Could not load image'));
  img.src = src;
});

const AssetCard = ({ kind, asset, onUpload }) => {
  const meta = ASSET_META[kind];
  const Icon = meta.icon;
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try { await onUpload(kind, file); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  return (
    <Card data-testid={`branding-asset-${kind}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <Icon className="h-6 w-6 text-violet-600 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">{meta.label}</p>
            <p className="text-[11px] text-gray-500">{meta.description}</p>
            {asset?.exists ? (
              <Badge className="mt-1 bg-emerald-100 text-emerald-700 text-[10px]">Updated {fmtDate(asset.uploaded_at)}</Badge>
            ) : (
              <Badge className="mt-1 bg-amber-100 text-amber-700 text-[10px]">Not uploaded</Badge>
            )}
          </div>
        </div>
        {asset?.exists && (
          <div className="rounded-lg border bg-gray-50 p-2 flex items-center justify-center">
            <img src={`${API}${asset.url}?t=${asset.uploaded_at}`} alt={meta.label} className="max-h-32 object-contain" data-testid={`branding-preview-${kind}`} />
          </div>
        )}
        <input ref={fileRef} type="file" accept={meta.accept} className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid={`branding-input-${kind}`} />
        <Button onClick={() => fileRef.current?.click()} disabled={busy} className="w-full bg-violet-600 hover:bg-violet-700" data-testid={`branding-upload-${kind}`}>
          <Upload className="h-4 w-4 mr-1" /> {busy ? 'Uploading…' : (asset?.exists ? 'Replace' : 'Upload')}
        </Button>
      </CardContent>
    </Card>
  );
};

const BrandingSettings = () => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [companies, setCompanies] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState('');
  const [signatures, setSignatures] = useState([]);
  const [stamps, setStamps] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add-company dialog
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({ name: '', full_name: '' });
  const [savingCompany, setSavingCompany] = useState(false);

  // Signature dialog
  const [showAddSig, setShowAddSig] = useState(false);
  const [editingSig, setEditingSig] = useState(null);
  const [sigForm, setSigForm] = useState({ name: '', designation: '', file: null });
  const [sigRotation, setSigRotation] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const sigFileRef = useRef(null);

  // Stamp library dialog
  const [showAddStamp, setShowAddStamp] = useState(false);
  const [editingStamp, setEditingStamp] = useState(null);
  const [stampForm, setStampForm] = useState({ name: '', file: null });
  const [submittingStamp, setSubmittingStamp] = useState(false);
  const stampFileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s, st] = await Promise.all([
        axios.get(`${API}/api/branding/companies`, { headers }),
        axios.get(`${API}/api/branding/signatures`, { headers }),
        axios.get(`${API}/api/branding/stamps`, { headers }),
      ]);
      const list = c.data || [];
      setCompanies(list);
      setSignatures(s.data || []);
      setStamps(st.data || []);
      setSelectedSlug((prev) => prev || (list.find(x => x.is_default) || list[0] || {}).slug || '');
    } catch (e) {
      toast({ title: 'Failed to load', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const selectedCompany = companies.find(c => c.slug === selectedSlug) || null;

  const uploadAsset = async (kind, file) => {
    if (!selectedSlug) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/api/branding/companies/${selectedSlug}/asset/${kind}`, fd, { headers });
      toast({ title: `${ASSET_META[kind].label} updated`, description: selectedCompany?.name });
      load();
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const addCompany = async (e) => {
    e?.preventDefault?.();
    if (!companyForm.name.trim()) { toast({ title: 'Company name is required', variant: 'destructive' }); return; }
    setSavingCompany(true);
    try {
      const res = await axios.post(`${API}/api/branding/companies`, { name: companyForm.name.trim(), full_name: companyForm.full_name.trim() }, { headers });
      toast({ title: 'Company added', description: companyForm.name.trim() });
      setShowAddCompany(false);
      setCompanyForm({ name: '', full_name: '' });
      setSelectedSlug(res.data?.slug || selectedSlug);
      load();
    } catch (e) {
      toast({ title: 'Could not add company', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setSavingCompany(false); }
  };

  const deleteCompany = async () => {
    if (!selectedCompany || selectedCompany.is_default) return;
    if (!window.confirm(`Delete company "${selectedCompany.name}" and its letterhead/stamp files? Certificates already generated are unaffected.`)) return;
    try {
      await axios.delete(`${API}/api/branding/companies/${selectedCompany.slug}`, { headers });
      toast({ title: 'Company deleted' });
      setSelectedSlug('');
      load();
    } catch (e) {
      toast({ title: 'Could not delete', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const openSigDialog = (sig) => {
    setEditingSig(sig);
    setSigForm({ name: sig?.name || '', designation: sig?.designation || '', file: null });
    setSigRotation(0);
    setShowAddSig(true);
  };

  const submitSig = async (e) => {
    e?.preventDefault?.();
    if (!sigForm.name.trim()) { toast({ title: 'Name is required', variant: 'destructive' }); return; }
    if (!editingSig && !sigForm.file) { toast({ title: 'Pick a signature PNG', variant: 'destructive' }); return; }
    setSubmitting(true);
    try {
      // Build the file to upload, baking in any rotation.
      let fileToUpload = sigForm.file;
      const rot = ((sigRotation % 360) + 360) % 360;
      if (rot !== 0 && (sigForm.file || editingSig?.url)) {
        const src = sigForm.file ? URL.createObjectURL(sigForm.file) : `${API}${editingSig.url}?t=${editingSig.uploaded_at}`;
        const blob = await rotateImageToBlob(src, rot);
        fileToUpload = new File([blob], sigForm.file?.name || `${editingSig?.slug || 'signature'}.png`, { type: 'image/png' });
        if (sigForm.file) URL.revokeObjectURL(src);
      }

      if (editingSig && !fileToUpload) {
        const fd = new FormData();
        fd.append('name', sigForm.name);
        fd.append('designation', sigForm.designation || '');
        await axios.put(`${API}/api/branding/signatures/${editingSig.slug}`, fd, { headers });
      } else {
        const fd = new FormData();
        fd.append('name', sigForm.name);
        fd.append('designation', sigForm.designation || '');
        fd.append('file', fileToUpload);
        await axios.post(`${API}/api/branding/signatures`, fd, { headers });
      }
      toast({ title: editingSig ? 'Signature updated' : 'Signature added' });
      setShowAddSig(false); setEditingSig(null);
      setSigForm({ name: '', designation: '', file: null });
      setSigRotation(0);
      if (sigFileRef.current) sigFileRef.current.value = '';
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || e?.message || '', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  const deleteSig = async (slug, name) => {
    if (!window.confirm(`Remove signature for ${name}? Letters issued by ${name} will no longer have a signature image.`)) return;
    try {
      await axios.delete(`${API}/api/branding/signatures/${slug}`, { headers });
      toast({ title: 'Removed' });
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const openStampDialog = (st) => {
    setEditingStamp(st);
    setStampForm({ name: st?.name || '', file: null });
    if (stampFileRef.current) stampFileRef.current.value = '';
    setShowAddStamp(true);
  };

  const submitStamp = async (e) => {
    e?.preventDefault?.();
    if (!stampForm.name.trim()) { toast({ title: 'Headline is required', variant: 'destructive' }); return; }
    if (!editingStamp && !stampForm.file) { toast({ title: 'Pick a stamp PNG', variant: 'destructive' }); return; }
    setSubmittingStamp(true);
    try {
      const fd = new FormData();
      fd.append('name', stampForm.name);
      if (editingStamp && !stampForm.file) {
        await axios.put(`${API}/api/branding/stamps/${editingStamp.slug}`, fd, { headers });
      } else {
        fd.append('file', stampForm.file);
        await axios.post(`${API}/api/branding/stamps`, fd, { headers });
      }
      toast({ title: editingStamp ? 'Stamp updated' : 'Stamp added' });
      setShowAddStamp(false); setEditingStamp(null); setStampForm({ name: '', file: null });
      if (stampFileRef.current) stampFileRef.current.value = '';
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || e?.message || '', variant: 'destructive' });
    } finally { setSubmittingStamp(false); }
  };

  const deleteStamp = async (slug, name) => {
    if (!window.confirm(`Delete stamp "${name}"? Documents already generated are unaffected.`)) return;
    try {
      await axios.delete(`${API}/api/branding/stamps/${slug}`, { headers });
      toast({ title: 'Removed' });
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  if (loading) return <div className="p-6 text-center text-gray-500" data-testid="branding-loading">Loading…</div>;

  // Signature preview source (with live rotation)
  const sigPreviewSrc = sigForm.file
    ? URL.createObjectURL(sigForm.file)
    : (editingSig?.url ? `${API}${editingSig.url}?t=${editingSig.uploaded_at}` : null);

  return (
    <div className="space-y-6 max-w-5xl" data-testid="branding-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Palette className="h-7 w-7 text-violet-600" /> Branding
        </h1>
        <p className="text-gray-600 mt-1">
          Manage per-company letterheads, stamps, and signatures used on Experience &amp; Salary Certificates.
        </p>
      </div>

      {/* Company letterhead sets */}
      <section>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-3">
          <div className="flex-1">
            <h2 className="text-lg font-semibold flex items-center gap-2"><Building2 className="h-5 w-5 text-violet-600" /> Company Letterheads &amp; Stamps</h2>
            <p className="text-xs text-gray-500">Each company has its own header, footer &amp; stamp. Pick the company when generating a certificate.</p>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-xs">Managing company</Label>
              <Select value={selectedSlug} onValueChange={setSelectedSlug}>
                <SelectTrigger className="w-56" data-testid="branding-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
                <SelectContent>
                  {companies.map(c => (
                    <SelectItem key={c.slug} value={c.slug}>{c.name}{c.is_default ? ' (default)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => { setCompanyForm({ name: '', full_name: '' }); setShowAddCompany(true); }} className="bg-violet-600 hover:bg-violet-700" data-testid="add-company-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Company
            </Button>
          </div>
        </div>

        {selectedCompany && (
          <>
            <div className="flex items-center justify-between mb-3 px-1">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">{selectedCompany.name}</span>
                {selectedCompany.full_name && selectedCompany.full_name !== selectedCompany.name && (
                  <span className="text-gray-400"> — {selectedCompany.full_name}</span>
                )}
              </p>
              {!selectedCompany.is_default && (
                <Button size="sm" variant="ghost" onClick={deleteCompany} className="text-rose-600" data-testid="delete-company-btn">
                  <Trash2 className="h-4 w-4 mr-1" /> Delete company
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.keys(ASSET_META).map(kind => (
                <AssetCard key={`${selectedSlug}-${kind}`} kind={kind} asset={selectedCompany[kind]} onUpload={uploadAsset} />
              ))}
            </div>
          </>
        )}
      </section>

      {/* Signatures */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold">Signatories</h2>
            <p className="text-xs text-gray-500">PNG signatures (transparent background) used on certificates — shared across all companies.</p>
          </div>
          <Button onClick={() => openSigDialog(null)} className="bg-violet-600 hover:bg-violet-700" data-testid="add-signature-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Signatory
          </Button>
        </div>

        {signatures.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-10 text-center text-gray-500 text-sm">No signatories yet. Add one to start signing certificates.</CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {signatures.map(s => (
              <Card key={s.slug} data-testid={`signature-card-${s.slug}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <FileSignature className="h-6 w-6 text-violet-600 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{s.name}</p>
                      <p className="text-[11px] text-gray-500 truncate">{s.designation || '—'}</p>
                      {s.exists ? (
                        <Badge className="mt-1 bg-emerald-100 text-emerald-700 text-[10px]">Updated {fmtDate(s.uploaded_at)}</Badge>
                      ) : (
                        <Badge className="mt-1 bg-amber-100 text-amber-700 text-[10px]">File missing</Badge>
                      )}
                    </div>
                  </div>
                  {s.exists && (
                    <div className="rounded-lg border bg-gray-50 p-2 flex items-center justify-center min-h-[100px]">
                      <img src={`${API}${s.url}?t=${s.uploaded_at}`} alt={s.name} className="max-h-24 object-contain" data-testid={`signature-preview-${s.slug}`} />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openSigDialog(s)} className="flex-1" data-testid={`signature-edit-${s.slug}`}>
                      Replace / Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteSig(s.slug, s.name)} className="text-rose-600" data-testid={`signature-delete-${s.slug}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Company Stamps library */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2"><Stamp className="h-5 w-5 text-violet-600" /> Company Stamps</h2>
            <p className="text-xs text-gray-500">Transparent PNG stamps, each with a headline — shared across all companies. Pick one from the dropdown when generating a certificate (leave unpicked for no stamp).</p>
          </div>
          <Button onClick={() => openStampDialog(null)} className="bg-violet-600 hover:bg-violet-700" data-testid="add-stamp-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Stamp
          </Button>
        </div>

        {stamps.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-10 text-center text-gray-500 text-sm">No stamps yet. Add one to use it on certificates.</CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stamps.map(st => (
              <Card key={st.slug} data-testid={`stamp-card-${st.slug}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <Stamp className="h-6 w-6 text-violet-600 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{st.name}</p>
                      {st.exists ? (
                        <Badge className="mt-1 bg-emerald-100 text-emerald-700 text-[10px]">Updated {fmtDate(st.uploaded_at)}</Badge>
                      ) : (
                        <Badge className="mt-1 bg-amber-100 text-amber-700 text-[10px]">File missing</Badge>
                      )}
                    </div>
                  </div>
                  {st.exists && (
                    <div className="rounded-lg border bg-gray-50 p-2 flex items-center justify-center min-h-[100px]">
                      <img src={`${API}${st.url}?t=${st.uploaded_at}`} alt={st.name} className="max-h-24 object-contain" data-testid={`stamp-preview-${st.slug}`} />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openStampDialog(st)} className="flex-1" data-testid={`stamp-edit-${st.slug}`}>
                      Replace / Rename
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteStamp(st.slug, st.name)} className="text-rose-600" data-testid={`stamp-delete-${st.slug}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Add company dialog */}
      <Dialog open={showAddCompany} onOpenChange={(o) => { if (!o) setShowAddCompany(false); }}>
        <DialogContent data-testid="company-dialog">
          <DialogHeader><DialogTitle>Add Company</DialogTitle>
            <DialogDescription>Create a new company letterhead set. You can upload its header, footer and stamp after adding it.</DialogDescription>
          </DialogHeader>
          <form onSubmit={addCompany} className="space-y-3">
            <div>
              <Label>Display Name</Label>
              <Input value={companyForm.name} onChange={e => setCompanyForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Seere Group" data-testid="company-name" />
            </div>
            <div>
              <Label>Full Legal Name (used in "For …")</Label>
              <Input value={companyForm.full_name} onChange={e => setCompanyForm(f => ({ ...f, full_name: e.target.value }))} placeholder="e.g. Seere Group LLC" data-testid="company-full-name" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowAddCompany(false)}>Cancel</Button>
              <Button type="submit" disabled={savingCompany} className="bg-violet-600 hover:bg-violet-700" data-testid="company-submit">
                {savingCompany ? 'Saving…' : 'Add company'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit signature dialog */}
      <Dialog open={showAddSig} onOpenChange={(open) => { if (!open) { setShowAddSig(false); setEditingSig(null); setSigRotation(0); } }}>
        <DialogContent data-testid="signature-dialog">
          <DialogHeader>
            <DialogTitle>{editingSig ? `Edit: ${editingSig.name}` : 'Add Signatory'}</DialogTitle>
            <DialogDescription>Upload a transparent PNG signature and optionally rotate it before saving.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitSig} className="space-y-3">
            <div>
              <Label>Full Name</Label>
              <Input value={sigForm.name} onChange={e => setSigForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Jubu Thankachen" required data-testid="signature-name" />
              <p className="text-[10px] text-gray-400 mt-1">Must exactly match the signatory name selected when generating a certificate.</p>
            </div>
            <div>
              <Label>Designation</Label>
              <Input value={sigForm.designation} onChange={e => setSigForm(f => ({ ...f, designation: e.target.value }))} placeholder="e.g. Group HR Manager" data-testid="signature-designation" />
            </div>
            <div>
              <Label>Signature PNG (transparent background)</Label>
              <input ref={sigFileRef} type="file" accept=".png" onChange={(e) => { setSigForm(f => ({ ...f, file: e.target.files?.[0] || null })); setSigRotation(0); }} className="block w-full text-sm py-2" data-testid="signature-file-input" />
              {editingSig && !sigForm.file && (
                <p className="text-[10px] text-gray-400 mt-1">Leave empty to keep the current image (you can still rotate it below).</p>
              )}
            </div>

            {/* Rotation preview + controls */}
            {sigPreviewSrc && (
              <div className="space-y-2">
                <Label>Preview &amp; rotate</Label>
                <div className="rounded-lg border bg-gray-50 p-3 flex items-center justify-center min-h-[120px] overflow-hidden">
                  <img
                    src={sigPreviewSrc}
                    alt="signature preview"
                    className="max-h-28 object-contain transition-transform"
                    style={{ transform: `rotate(${sigRotation}deg)` }}
                    data-testid="signature-rotate-preview"
                  />
                </div>
                <div className="flex items-center justify-center gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setSigRotation(r => r - 90)} data-testid="signature-rotate-left">
                    <RotateCcw className="h-4 w-4 mr-1" /> Rotate left
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setSigRotation(r => r + 90)} data-testid="signature-rotate-right">
                    <RotateCw className="h-4 w-4 mr-1" /> Rotate right
                  </Button>
                  {sigRotation % 360 !== 0 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => setSigRotation(0)} data-testid="signature-rotate-reset">Reset</Button>
                  )}
                </div>
                {sigRotation % 360 !== 0 && (
                  <p className="text-[10px] text-center text-amber-600">Rotation of {((sigRotation % 360) + 360) % 360}° will be saved into the signature image.</p>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { setShowAddSig(false); setEditingSig(null); setSigRotation(0); }}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-violet-600 hover:bg-violet-700" data-testid="signature-submit">
                {submitting ? 'Saving…' : (editingSig ? 'Save changes' : 'Add signatory')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit stamp dialog */}
      <Dialog open={showAddStamp} onOpenChange={(open) => { if (!open) { setShowAddStamp(false); setEditingStamp(null); } }}>
        <DialogContent data-testid="stamp-dialog">
          <DialogHeader>
            <DialogTitle>{editingStamp ? `Edit: ${editingStamp.name}` : 'Add Company Stamp'}</DialogTitle>
            <DialogDescription>Give the stamp a headline to identify it, and upload a transparent PNG. It becomes selectable on certificates.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitStamp} className="space-y-3">
            <div>
              <Label>Headline / Label</Label>
              <Input value={stampForm.name} onChange={e => setStampForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Seere Group – Round Stamp" required data-testid="stamp-name" />
              <p className="text-[10px] text-gray-400 mt-1">This is what you'll see in the stamp dropdown when generating a document.</p>
            </div>
            <div>
              <Label>Stamp PNG (transparent background)</Label>
              <input ref={stampFileRef} type="file" accept=".png" onChange={(e) => setStampForm(f => ({ ...f, file: e.target.files?.[0] || null }))} className="block w-full text-sm py-2" data-testid="stamp-file-input" />
              {editingStamp && !stampForm.file && (
                <p className="text-[10px] text-gray-400 mt-1">Leave empty to keep the current image and just rename the headline.</p>
              )}
            </div>
            {(stampForm.file || editingStamp?.url) && (
              <div className="rounded-lg border bg-gray-50 p-3 flex items-center justify-center min-h-[120px]">
                <img
                  src={stampForm.file ? URL.createObjectURL(stampForm.file) : `${API}${editingStamp.url}?t=${editingStamp.uploaded_at}`}
                  alt="stamp preview"
                  className="max-h-28 object-contain"
                  data-testid="stamp-preview-img"
                />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { setShowAddStamp(false); setEditingStamp(null); }}>Cancel</Button>
              <Button type="submit" disabled={submittingStamp} className="bg-violet-600 hover:bg-violet-700" data-testid="stamp-submit">
                {submittingStamp ? 'Saving…' : (editingStamp ? 'Save changes' : 'Add stamp')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrandingSettings;
